/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Params,
  UrlTree,
  RouterStateSnapshot,
} from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import moment from 'moment';
import { AuthenticationService, RESTClientConfig } from 'rest-client';
import { from, Observable, of } from 'rxjs';

import {
  SetAgentTokenAction,
  SetAppModeAction,
  SetClientAppModeAction,
  SetIsMasBlauAction,
  SetPromoCodeAction,
  SetSelectedUpAction,
  SetSourceModeAction,
} from '@core/states/trip-state/trip.actions';
import { ProcessQueryParams } from '@core/utils/process-queryparams';

import { GoogleTagManagerService } from '../../services/analytics/google-tag-manager.service';
import { AppConfigService } from '../../services/app-config/app-config.service';
import { SetReferalAction } from '../../states/app-state/app.actions';

const URBAN_PARKING = 'urban';
const LANDING_ROUTE = '/where-when';
const CONFIRMATION_ROUTE = '/confirmation';
const SERVICES_ROUTE = '/services';
const TRAVELER_DATA_ROUTE = '/traveler-data';
const STATE_SNAPSHOT = 'state_snapshot';
const STATE_TIMESTAMP = 'state_timestamp';
const GET_SECONDS = 1000;
const GET_MINUTES = 60;
const MAX_MINUTES_VALID_STORE = 15;

@UntilDestroy()
@Injectable()
export class MandatoryParamsGuard implements CanActivate {
  urlExceptions: string[];

  constructor(
    private store: Store,
    private processQueryParams: ProcessQueryParams,
    private restConfig: RESTClientConfig,
    private authService: AuthenticationService,
    private appConfigService: AppConfigService,
    private gtmService: GoogleTagManagerService
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Observable<UrlTree> {
    const stateSnapshot = localStorage.getItem(STATE_SNAPSHOT);
    if (
      window.performance &&
      window.performance.getEntriesByType('navigation').length
    ) {
      const from: any = window.performance.getEntriesByType('navigation')[0];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (from?.type === 'reload') {
        if (stateSnapshot && stateSnapshot !== '') {
          const timeFromLast =
            (new Date().getTime() -
              Number(localStorage.getItem(STATE_TIMESTAMP))) /
            GET_SECONDS /
            GET_MINUTES;
          if (
            timeFromLast < MAX_MINUTES_VALID_STORE &&
            !route.queryParams?.agent_token
          ) {
            this.store.reset(JSON.parse(stateSnapshot));
            return of(true);
          } else {
            localStorage.removeItem(STATE_SNAPSHOT);
            localStorage.removeItem(STATE_TIMESTAMP);
          }
        }
      } else {
        localStorage.removeItem(STATE_SNAPSHOT);
        localStorage.removeItem(STATE_TIMESTAMP);
      }
    }
    this.initExceptions(route.queryParams);
    this.readQueryParams(route.queryParams);

    if (this.urlExceptions.includes(state.url.split('?')[0])) {
      return of(true);
    } else {
      return this.processQueryParams.process(
        route.queryParams
      ) as Observable<boolean>;
    }
  }

  private readQueryParams(queryParams: Params): void {
    const {
      source_mode,
      up,
      mb,
      agent_token,
      app_user_token,
      app_mode,
      client_app_mode,
      app_version,
      form_version,
      app_name,
      device_type,
      utm_medium,
      utm_source,
      utm_campaign,
      utm_term,
      promocode,
    } = queryParams;

    if (Object.keys(queryParams).length !== 0) {
      this.store.dispatch(new SetReferalAction(queryParams));
    }
    if (source_mode) {
      this.store.dispatch(new SetSourceModeAction(source_mode as string));
    }
    if (promocode) {
      let promocode_request_timestamp= Date.now();
      this.store.dispatch(new SetPromoCodeAction(promocode as string, promocode_request_timestamp as unknown as string));
    }
    if (up) {
      this.store.dispatch(new SetSelectedUpAction(up));
    }
    if (mb) {
      this.store.dispatch([
        new SetIsMasBlauAction(true),
        new SetSourceModeAction(URBAN_PARKING),
      ]);
    }
    if (agent_token) {
      this.store.dispatch(new SetAgentTokenAction(true));
    }
    if (app_user_token) {
      this.setAppUserToken(app_user_token);
    }
    if (app_mode || client_app_mode) {
      if (app_mode) {
        this.store.dispatch(new SetAppModeAction(app_mode));
      } else {
        this.store.dispatch(new SetClientAppModeAction(client_app_mode));
      }
      this.restConfig.setAppVersion(app_version);
      this.restConfig.setAppName(app_name);
      this.restConfig.setDeviceType(device_type);
    }

    if (form_version) {
      this.appConfigService.setFormVersionStorage(form_version);
    }

    if (utm_medium || utm_source || utm_campaign || utm_term) {
      const utm = JSON.parse(localStorage.getItem('utm'));
      if (
        utm === null ||
        utm.utm_medium !== utm_medium ||
        utm.utm_source !== utm_source ||
        utm.utm_campaign !== utm_campaign ||
        utm.utm_term !== utm_term
      ) {
        localStorage.setItem(
          'utm',
          JSON.stringify({
            utm_medium: utm_medium,
            utm_source: utm_source,
            utm_campaign: utm_campaign,
            utm_term: utm_term,
            url: window.location.href,
            first_seen_timestamp: Date.now(),
            last_seen_timestamp: Date.now(),
            timestamp_send: null,
          })
        );
      } else {
        if (
          utm &&
          utm.last_seen_timestamp &&
          this.lessThanSomeHoursAgo(utm.last_seen_timestamp, 4)
        ) {
          this.gtmService.pushOnDataLayer({
            utm_campaign: utm_campaign,
            utm_medium: utm_medium,
            utm_source: utm_source,
            utm_term: utm_term,
          });
        }

        const _utm = { ...utm };
        _utm.last_seen_timestamp = Date.now();
        localStorage.setItem('utm', JSON.stringify(_utm));
      }
    }
  }

  private lessThanSomeHoursAgo(date: any, _hours: number): boolean {
    const hours = moment().diff(moment(date), 'hours', true);
    return hours < _hours;
  }

  private initExceptions(params: Params): void {
    this.urlExceptions = [LANDING_ROUTE, CONFIRMATION_ROUTE];
    if (params.modify_res_token || params.agent_token || params.absuid) {
      this.logout();
      this.urlExceptions.push(SERVICES_ROUTE);
    }
    if (
      (params.focus && params.focus === 'billing_data') ||
      params.modify_res_token ||
      params.absuid
    ) {
      this.urlExceptions.push(TRAVELER_DATA_ROUTE);
    }
  }

  private logout(): void {
    from(this.authService.logout())
      .pipe(untilDestroyed(this))
      .subscribe(() => console.log('logout'));
  }

  private setAppUserToken(token: string): void {
    const m = new Date();
    m.setDate(m.getDate() + 1);
    const mockExpire =
      m.getUTCFullYear() +
      '/' +
      ('0' + (m.getUTCMonth() + 1)).slice(-2) +
      '/' +
      ('0' + m.getUTCDate()).slice(-2) +
      ' ' +
      ('0' + m.getUTCHours()).slice(-2) +
      ':' +
      ('0' + m.getUTCMinutes()).slice(-2) +
      ':' +
      ('0' + m.getUTCSeconds()).slice(-2);
    const userToken = {
      status: 'ok',
      access_token: token,
      expires_in: mockExpire,
    };
    localStorage.setItem('auth_token', JSON.stringify(userToken));
  }
}
